import * as React from "react";
import fetch from "isomorphic-fetch";
import Client from "shopify-buy";
import type {
  ShopifyCheckout,
  ShopifyClient,
  ShopifyCustomAttribute,
  ShopifyLineItemInput,
} from "src/types/shopify";
import {
  shopifyStoreDomain,
  shopifyStorefrontAccessToken,
} from "src/config/shopify";

const client: ShopifyClient = Client.buildClient(
  {
    domain: shopifyStoreDomain,
    storefrontAccessToken: shopifyStorefrontAccessToken,
  },
  fetch
);

export interface StoreContextValue {
  loading: boolean;
  didJustAddToCart: boolean;
  addVariantToCart: (
    variantId: string,
    quantity: number | string,
    customAttributes?: ShopifyCustomAttribute[]
  ) => Promise<void>;
  removeLineItem: (checkoutId: string, lineItemId: string) => Promise<void>;
  updateLineItem: (
    checkoutId: string,
    lineItemId: string,
    quantity: number | string
  ) => Promise<void>;
  setDiscount: (discountCode: string) => Promise<void>;
  client: ShopifyClient;
  checkout: ShopifyCheckout;
}

const defaultCheckout: ShopifyCheckout = {
  id: "",
  lineItems: [],
  webUrl: "",
  subtotalPriceV2: { amount: "0", currencyCode: "GBP" },
  totalPriceV2: { amount: "0", currencyCode: "GBP" },
};

export const StoreContext = React.createContext<StoreContextValue | undefined>(
  undefined
);

export function useStore(): StoreContextValue {
  const context = React.useContext(StoreContext);

  if (!context) {
    throw new Error("useStore must be used within a StoreProvider");
  }

  return context;
}

const isBrowser = typeof window !== "undefined";
const localStorageKey = "shopify_checkout_id";

export const StoreProvider = ({
  children,
}: React.PropsWithChildren): React.ReactElement => {
  const [checkout, setCheckout] = React.useState<ShopifyCheckout>(defaultCheckout);
  const [loading, setLoading] = React.useState(false);
  const [didJustAddToCart, setDidJustAddToCart] = React.useState(false);

  const setCheckoutItem = (nextCheckout: ShopifyCheckout) => {
    if (isBrowser) {
      localStorage.setItem(localStorageKey, nextCheckout.id);
    }

    setCheckout(nextCheckout);
  };

  React.useEffect(() => {
    if (process.env.GATSBY_SKIP_SHOPIFY === "true") {
      return;
    }

    const initializeCheckout = async () => {
      const existingCheckoutID = isBrowser
        ? localStorage.getItem(localStorageKey)
        : null;

      if (existingCheckoutID && existingCheckoutID !== "null") {
        try {
          const existingCheckout = await client.checkout.fetch(existingCheckoutID);
          if (!existingCheckout.completedAt) {
            setCheckoutItem(existingCheckout);
            return;
          }
        } catch (error) {
          if (isBrowser) {
            localStorage.setItem(localStorageKey, "null");
          }
        }
      }

      const newCheckout = await client.checkout.create();
      setCheckoutItem(newCheckout);
    };

    void initializeCheckout().catch((error: unknown) => {
      console.error("Unable to initialize the Shopify checkout", error);
    });
  }, []);

  const runCheckoutMutation = async (
    mutation: () => Promise<ShopifyCheckout>,
    onSuccess?: () => void
  ): Promise<void> => {
    setLoading(true);

    try {
      const nextCheckout = await mutation();
      setCheckoutItem(nextCheckout);
      onSuccess?.();
    } finally {
      setLoading(false);
    }
  };

  const setDiscount = (discountCode: string): Promise<void> =>
    runCheckoutMutation(() =>
      client.checkout.addDiscount(checkout.id, discountCode)
    );

  const addVariantToCart = (
    variantId: string,
    quantity: number | string,
    customAttributes?: ShopifyCustomAttribute[]
  ): Promise<void> => {
    const lineItemsToUpdate: ShopifyLineItemInput[] = [
      {
        variantId,
        quantity: parseInt(String(quantity), 10),
        customAttributes,
      },
    ];

    return runCheckoutMutation(
      () => client.checkout.addLineItems(checkout.id, lineItemsToUpdate),
      () => {
        setDidJustAddToCart(true);
        window.setTimeout(() => setDidJustAddToCart(false), 3000);
      }
    );
  };

  const removeLineItem = (
    checkoutId: string,
    lineItemId: string
  ): Promise<void> => {
    return runCheckoutMutation(() =>
      client.checkout.removeLineItems(checkoutId, [lineItemId])
    );
  };

  const updateLineItem = (
    checkoutId: string,
    lineItemId: string,
    quantity: number | string
  ): Promise<void> => {
    return runCheckoutMutation(() =>
      client.checkout.updateLineItems(checkoutId, [
        { id: lineItemId, quantity: parseInt(String(quantity), 10) },
      ])
    );
  };

  const value: StoreContextValue = {
    client,
    addVariantToCart,
    removeLineItem,
    updateLineItem,
    checkout,
    loading,
    didJustAddToCart,
    setDiscount,
  };

  return <StoreContext.Provider value={value}>{children}</StoreContext.Provider>;
};
